import { StoreBadges } from "@components/shared/StoreBadges";
import { cn } from "@/ui/utils/cn";
import { ReactNode } from "react";

export type Fact = { value: string; label: string };

const TONE_CLASS = { white: "bg-white", mist: "bg-mist" } as const;

/** The product's parameters as a definition list; nothing when there are none. */
const Facts = ({ facts = [] }: { facts?: Fact[] }) => {
  if (facts.length === 0) {
    return null;
  }
  return (
    <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6 border-t border-slate-200 pt-8 sm:grid-cols-4">
      {facts.map((fact) => (
        <div key={fact.label} className="flex flex-col">
          <dt className="order-2 mt-1 text-sm text-slate-500">{fact.label}</dt>
          <dd className="tabular order-1 text-xl font-semibold text-ink md:text-2xl">
            {fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
};

/**
 * Product page opener: product name, promise, the product's parameters as a
 * definition list, and the store badges. `aside` takes an illustration.
 */
export const ProductHero = ({
  eyebrow,
  title,
  subtitle,
  facts,
  aside,
  placement,
  cta,
  tone = "white",
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  facts?: Fact[];
  aside?: ReactNode;
  placement: string;
  /** Replaces the store badges, e.g. a link to a form further down. */
  cta?: ReactNode;
  tone?: keyof typeof TONE_CLASS;
}) => (
  <section className={cn("overflow-hidden", TONE_CLASS[tone])}>
    <div
      className={cn(
        "container grid gap-12 py-14 md:py-24",
        aside ? "md:grid-cols-[1.1fr_0.9fr] md:items-center" : undefined,
      )}
    >
      <div className="max-w-2xl">
        <p className="text-lg font-semibold text-mint-ink md:text-xl">
          {eyebrow}
        </p>
        <h1 className="display mt-3 text-display-sm md:text-display">
          {title}
        </h1>
        <p className="mt-6 max-w-prose text-body text-slate-600 md:text-lg md:leading-relaxed">
          {subtitle}
        </p>
        <div className="mt-8">
          {cta ?? <StoreBadges placement={placement} />}
        </div>
        <Facts facts={facts} />
      </div>
      {aside ? (
        <div className="flex justify-center md:justify-end">{aside}</div>
      ) : null}
    </div>
  </section>
);
